import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import { PanelLeft } from "lucide-react";
import { cn } from "../../lib/utils";

/* ─── Context ─────────────────────────────────────────────────── */
const SIDEBAR_WIDTH        = "16rem";
const SIDEBAR_WIDTH_ICON   = "3.5rem";
const SIDEBAR_COOKIE       = "sidebar_open";

const SidebarContext = React.createContext(null);

function useSidebar() {
  const ctx = React.useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebar must be used inside <SidebarProvider>");
  return ctx;
}

/* ─── Provider ───────────────────────────────────────────────── */
const SidebarProvider = React.forwardRef(({ defaultOpen = true, open: openProp, onOpenChange, className, children, ...props }, ref) => {
  const [_open, _setOpen] = React.useState(() => {
    try { return localStorage.getItem(SIDEBAR_COOKIE) !== "false"; }
    catch { return defaultOpen; }
  });

  const open = openProp ?? _open;
  const setOpen = React.useCallback((val) => {
    const next = typeof val === "function" ? val(open) : val;
    _setOpen(next);
    onOpenChange?.(next);
    try { localStorage.setItem(SIDEBAR_COOKIE, String(next)); } catch {}
  }, [open, onOpenChange]);

  const toggleSidebar = React.useCallback(() => setOpen(o => !o), [setOpen]);

  return (
    <SidebarContext.Provider value={{ open, setOpen, toggleSidebar }}>
      <div
        ref={ref}
        style={{
          "--sidebar-width": SIDEBAR_WIDTH,
          "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
        }}
        className={cn("flex h-screen w-full overflow-hidden bg-gray-50", className)}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
});
SidebarProvider.displayName = "SidebarProvider";

/* ─── Sidebar ────────────────────────────────────────────────── */
const Sidebar = React.forwardRef(({ className, children, ...props }, ref) => {
  const { open } = useSidebar();
  return (
    <motion.aside
      ref={ref}
      animate={{ width: open ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_ICON }}
      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      className={cn(
        "relative flex h-screen flex-col border-r border-gray-200 bg-white overflow-hidden shrink-0",
        className
      )}
      {...props}
    >
      {children}
    </motion.aside>
  );
});
Sidebar.displayName = "Sidebar";

/* ─── Trigger ────────────────────────────────────────────────── */
const SidebarTrigger = React.forwardRef(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();
  return (
    <button
      ref={ref}
      onClick={(e) => { toggleSidebar(); onClick?.(e); }}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-md bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors focus:outline-none",
        className
      )}
      {...props}
    >
      <PanelLeft className="h-4 w-4" />
      <span className="sr-only">Toggle sidebar</span>
    </button>
  );
});
SidebarTrigger.displayName = "SidebarTrigger";

/* ─── Inset (main content) ───────────────────────────────────── */
const SidebarInset = React.forwardRef(({ className, ...props }, ref) => (
  <main
    ref={ref}
    className={cn("flex flex-1 flex-col overflow-hidden min-w-0", className)}
    {...props}
  />
));
SidebarInset.displayName = "SidebarInset";

/* ─── Header / Content / Footer ─────────────────────────────── */
const SidebarHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-2 p-3 border-b border-gray-100", className)} {...props} />
));
SidebarHeader.displayName = "SidebarHeader";

const SidebarContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-1 flex-col overflow-y-auto overflow-x-hidden gap-1 p-2", className)} {...props} />
));
SidebarContent.displayName = "SidebarContent";

const SidebarFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-2 p-2 border-t border-gray-100", className)} {...props} />
));
SidebarFooter.displayName = "SidebarFooter";

/* ─── Group ──────────────────────────────────────────────────── */
const SidebarGroup = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-0.5", className)} {...props} />
));
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = React.forwardRef(({ className, ...props }, ref) => {
  const { open } = useSidebar();
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.span
          ref={ref}
          key="label"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className={cn(
            "px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 select-none",
            className
          )}
          {...props}
        />
      )}
    </AnimatePresence>
  );
});
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-0.5", className)} {...props} />
));
SidebarGroupContent.displayName = "SidebarGroupContent";

/* ─── Menu ───────────────────────────────────────────────────── */
const SidebarMenu = React.forwardRef(({ className, ...props }, ref) => (
  <ul ref={ref} className={cn("flex flex-col gap-0.5 list-none m-0 p-0", className)} {...props} />
));
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = React.forwardRef(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("", className)} {...props} />
));
SidebarMenuItem.displayName = "SidebarMenuItem";

/* ─── MenuButton ─────────────────────────────────────────────── */
const SidebarMenuButton = React.forwardRef(({
  className, isActive, tooltip, size = "default", children, asChild = false, onClick, ...props
}, ref) => {
  const { open } = useSidebar();

  return (
    <button
      ref={ref}
      onClick={onClick}
      data-active={isActive || undefined}
      className={cn(
        "relative flex w-full items-center gap-2.5 rounded-md bg-transparent text-[13px] font-medium transition-colors focus:outline-none",
        size === "lg" ? "px-3 py-2.5" : "px-3 py-2",
        isActive
          ? "bg-blue-50 text-blue-600"
          : "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
        !open && "justify-center px-0",
        className
      )}
      title={!open && tooltip ? tooltip : undefined}
      {...props}
    >
      {children}
    </button>
  );
});
SidebarMenuButton.displayName = "SidebarMenuButton";

/* ─── Label inside menu button (animated hide/show) ─────────── */
const SidebarMenuLabel = React.forwardRef(({ className, children, ...props }, ref) => {
  const { open } = useSidebar();
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.span
          ref={ref}
          key="label"
          initial={{ opacity: 0, x: -6 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -6 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className={cn("truncate", className)}
          {...props}
        >
          {children}
        </motion.span>
      )}
    </AnimatePresence>
  );
});
SidebarMenuLabel.displayName = "SidebarMenuLabel";

/* ─── Separator ──────────────────────────────────────────────── */
const SidebarSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("my-1 h-px bg-gray-100 mx-2", className)} {...props} />
));
SidebarSeparator.displayName = "SidebarSeparator";

export {
  SidebarProvider, Sidebar, SidebarTrigger, SidebarInset,
  SidebarHeader, SidebarContent, SidebarFooter,
  SidebarGroup, SidebarGroupLabel, SidebarGroupContent,
  SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarMenuLabel,
  SidebarSeparator, useSidebar,
};
